import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CheckCircle2,
    ExternalLink,
    FileText,
    Link2,
    Loader2,
    RefreshCw,
    Search,
    Sparkles,
    User,
    Car,
    Hash,
} from "lucide-react";
import { toast } from "sonner";
import { epostaMerkez } from "../../lib/api";

const REASON_LABELS = {
    dosya_no: "Dosya no",
    dosya_no_partial: "Dosya no (kısmi)",
    plaka: "Plaka",
    muvekkil: "Müvekkil",
    tc: "TC",
    talep_no: "Talep no",
    police_no: "Poliçe",
    ogrenme_gonderici: "Öğrenilen gönderici",
    ogrenme_domain: "Öğrenilen domain",
    ogrenme_plaka: "Öğrenilen plaka",
    ogrenme_konu: "Öğrenilen konu",
    basvuru_takip: "Başvuru takip",
};

function Chip({ children, tone = "slate" }) {
    const tones = {
        slate: "bg-slate-500/15 text-slate-300 border-slate-500/20",
        gold: "bg-[#D4AF37]/15 text-[#F3E5AB] border-[#D4AF37]/30",
        sky: "bg-sky-500/15 text-sky-200 border-sky-500/30",
        emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
        amber: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] border ${tones[tone] || tones.slate}`}>
            {children}
        </span>
    );
}

function ScoreBar({ score }) {
    const s = Math.min(100, Math.max(0, Number(score) || 0));
    const color = s >= 75 ? "bg-emerald-500" : s >= 50 ? "bg-amber-500" : "bg-slate-500";
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full ${color} transition-all`} style={{ width: `${s}%` }} />
            </div>
            <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{s}%</span>
        </div>
    );
}

export default function EpostaDetailPanel({
    emailId,
    onUpdated,
    onaylaBagla,
    applyTalepNo,
    applyBusy,
}) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dosyaQuery, setDosyaQuery] = useState("");
    const [dosyaHits, setDosyaHits] = useState([]);
    const [dosyaSearchBusy, setDosyaSearchBusy] = useState(false);
    const [suggestBusy, setSuggestBusy] = useState(false);
    const [akilBusy, setAkilBusy] = useState(false);

    const loadDetail = useCallback(async () => {
        if (!emailId) {
            setDetail(null);
            return;
        }
        setLoading(true);
        try {
            const d = await epostaMerkez.detay(emailId);
            setDetail(d);
        } catch {
            setDetail(null);
            toast.error("E-posta detayı yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [emailId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    useEffect(() => {
        if (!dosyaQuery || dosyaQuery.trim().length < 2) {
            setDosyaHits([]);
            return undefined;
        }
        const t = setTimeout(async () => {
            setDosyaSearchBusy(true);
            try {
                const r = await epostaMerkez.dosyaAra(dosyaQuery.trim());
                setDosyaHits(r.items || []);
            } catch {
                setDosyaHits([]);
            } finally {
                setDosyaSearchBusy(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [dosyaQuery]);

    async function handleAkilAnaliz() {
        if (!emailId) return;
        setAkilBusy(true);
        try {
            const res = await epostaMerkez.akilAnaliz(emailId);
            if (res.success) {
                toast.success(res.tespit_ozeti || "Akıl analizi tamamlandı.");
            } else {
                toast.error(res.error || "Akıl analizi başarısız.");
            }
            await loadDetail();
            onUpdated?.();
        } catch (err) {
            toast.error(err?.response?.data?.error || "Akıl analizi başarısız.");
        } finally {
            setAkilBusy(false);
        }
    }

    async function handleSuggest() {
        if (!emailId) return;
        setSuggestBusy(true);
        try {
            await epostaMerkez.suggest(emailId);
            toast.success("Eşleşmeler güncellendi.");
            await loadDetail();
            onUpdated?.();
        } catch {
            toast.error("Eşleşme yenilenemedi.");
        } finally {
            setSuggestBusy(false);
        }
    }

    if (!emailId) {
        return (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center p-8 rounded-xl border border-dashed border-white/10 bg-[#0B0F19]/40">
                <FileText className="h-12 w-12 text-slate-600 mb-3" />
                <p className="text-slate-400 text-sm">Soldan bir e-posta seçin</p>
                <p className="text-slate-500 text-xs mt-1 max-w-xs">
                    Tam içerik, parse edilen alanlar ve gelişmiş dosya bağlama burada görünür.
                </p>
            </div>
        );
    }

    if (loading || !detail) {
        return (
            <div className="h-full min-h-[420px] flex items-center justify-center rounded-xl border border-white/10 bg-[#121A2F]">
                <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
            </div>
        );
    }

    const plakalar = detail.parsed_plakalar || [];
    const dosyaNos = detail.parsed_dosya_nos || [];
    const refs = detail.parsed_referanslar || [];

    return (
        <div className="h-full flex flex-col rounded-xl border border-white/10 bg-[#121A2F] overflow-hidden">
            <div className="shrink-0 p-4 border-b border-white/10 bg-[#0B0F19]/60">
                <div className="flex flex-wrap items-start gap-2 mb-2">
                    <h2 className="text-base font-semibold text-white flex-1 leading-snug">
                        {detail.subject || "(Konu yok)"}
                    </h2>
                    {detail.mailbox && (
                        <Chip tone={detail.mailbox === "hotmail" ? "sky" : "emerald"}>{detail.mailbox}</Chip>
                    )}
                    {detail.match_status === "MATCHED" && (
                        <Chip tone="emerald">Eşleşti</Chip>
                    )}
                    {detail.match_status === "PENDING" && (
                        <Chip tone="amber">Bekliyor</Chip>
                    )}
                    {detail.match_status === "IGNORED" && (
                        <Chip tone="slate">Yok sayıldı</Chip>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    <button
                        type="button"
                        onClick={handleAkilAnaliz}
                        disabled={akilBusy}
                        className="h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    >
                        {akilBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        Akıl analiz
                    </button>
                </div>
                <div className="text-xs text-slate-400 space-y-1">
                    <div className="truncate">
                        <span className="text-slate-500">Kimden: </span>
                        {detail.from_email || "—"}
                    </div>
                    <div>
                        <span className="text-slate-500">Tarih: </span>
                        {detail.created_at
                            ? new Date(detail.created_at).toLocaleString("tr-TR")
                            : "—"}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                <section>
                    <h3 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Sistem analizi
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                        {dosyaNos.map((n) => (
                            <Chip key={n} tone="gold">
                                <Hash className="h-2.5 w-2.5 mr-1 inline" />
                                {n}
                            </Chip>
                        ))}
                        {plakalar.map((p) => (
                            <Chip key={p} tone="sky">
                                <Car className="h-2.5 w-2.5 mr-1 inline" />
                                {p}
                            </Chip>
                        ))}
                        {detail.sigorta_talep_no && (
                            <Chip tone="gold">Talep: {detail.sigorta_talep_no}</Chip>
                        )}
                        {detail.sigorta_sirketi && (
                            <Chip>{detail.sigorta_sirketi}</Chip>
                        )}
                        {!dosyaNos.length && !plakalar.length && !detail.sigorta_talep_no && (
                            <span className="text-xs text-slate-500">Otomatik alan bulunamadı</span>
                        )}
                    </div>
                    {detail.karar_ozeti && (
                        <p className="mt-2 text-xs text-slate-400 leading-relaxed border-l-2 border-[#D4AF37]/40 pl-3">
                            {detail.karar_ozeti}
                        </p>
                    )}
                    {refs.length > 0 && (
                        <ul className="mt-2 text-[10px] text-slate-500 space-y-0.5">
                            {refs.map((r, i) => (
                                <li key={i}>
                                    {r.tip}: <span className="text-slate-300">{r.deger}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section>
                    <h3 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                        <FileText className="h-3 w-3" /> E-posta içeriği
                    </h3>
                    <div className="rounded-lg border border-white/10 bg-[#0B0F19] p-4 max-h-[280px] overflow-y-auto">
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">
                            {detail.body_text || detail.body_snippet || "(İçerik yok)"}
                        </pre>
                    </div>
                </section>

                <section className="rounded-lg border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-[#F3E5AB] flex items-center gap-2">
                            <Link2 className="h-4 w-4" /> Sigorta dosyasına bağla
                        </h3>
                        <button
                            type="button"
                            onClick={handleSuggest}
                            disabled={suggestBusy}
                            className="text-[10px] text-slate-400 hover:text-[#D4AF37] inline-flex items-center gap-1"
                        >
                            {suggestBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3 w-3" />
                            )}
                            Yeniden eşleştir
                        </button>
                    </div>

                    {detail.matched_dosya_id ? (
                        <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono text-sm text-emerald-200">
                                        {detail.matched_dosya_no}
                                    </div>
                                    {detail.matched_muvekkil && (
                                        <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                            <User className="h-3 w-3" />
                                            {detail.matched_muvekkil}
                                            {detail.matched_plaka && ` · ${detail.matched_plaka}`}
                                        </div>
                                    )}
                                </div>
                                <Link
                                    to={`/e/sigorta-dosyalari/${detail.matched_dosya_id}`}
                                    className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                                >
                                    Dosya <ExternalLink className="h-3 w-3" />
                                </Link>
                            </div>
                            {detail.sigorta_talep_no && (
                                <button
                                    type="button"
                                    disabled={applyBusy}
                                    onClick={() => applyTalepNo?.(detail)}
                                    className="w-full h-9 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-semibold inline-flex items-center justify-center gap-2"
                                >
                                    {applyBusy ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="h-3 w-3" />
                                    )}
                                    Talep / referansı dosyaya onayla
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {(detail.suggested_matches || []).length > 0 && (
                                <div className="mb-4 space-y-2">
                                    <p className="text-[10px] uppercase text-slate-500">Önerilen dosyalar</p>
                                    {detail.suggested_matches.slice(0, 8).map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => onaylaBagla?.(detail.id, m.id, null)}
                                            className="w-full text-left p-3 rounded-lg border border-white/10 bg-[#0B0F19]/80 hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 transition-colors"
                                        >
                                            <div className="flex justify-between gap-2 mb-1">
                                                <span className="font-mono text-xs text-[#F3E5AB]">
                                                    {m.dosya_no}
                                                </span>
                                                <span className="text-[10px] text-slate-400">{m.plaka}</span>
                                            </div>
                                            <div className="text-[10px] text-slate-500 mb-2 truncate">
                                                {m.muvekkil}
                                            </div>
                                            <ScoreBar score={m.score} />
                                            {(m.reasons || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {m.reasons.map((r) => (
                                                        <Chip key={r}>{REASON_LABELS[r] || r}</Chip>
                                                    ))}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <p className="text-[10px] uppercase text-slate-500 mb-2">Manuel dosya ara</p>
                            <div className="relative mb-2">
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
                                <input
                                    value={dosyaQuery}
                                    onChange={(e) => setDosyaQuery(e.target.value)}
                                    placeholder="Dosya no, plaka veya müvekkil adı…"
                                    className="w-full h-9 pl-8 pr-3 rounded-lg border border-white/10 bg-[#0B0F19] text-xs text-slate-200"
                                />
                            </div>
                            {dosyaSearchBusy && (
                                <div className="text-xs text-slate-500 flex items-center gap-2 py-2">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Aranıyor…
                                </div>
                            )}
                            {dosyaHits.map((d) => (
                                <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => onaylaBagla?.(detail.id, d.id, d.dosya_no)}
                                    className="w-full text-left px-3 py-2 rounded border border-white/5 hover:bg-white/5 text-xs mb-1 flex justify-between gap-2"
                                >
                                    <span>
                                        <span className="font-mono text-[#F3E5AB]">{d.dosya_no}</span>
                                        <span className="text-slate-500 ml-2">{d.muvekkil}</span>
                                    </span>
                                    <span className="text-slate-500">{d.plaka}</span>
                                </button>
                            ))}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
